import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSystemUserQuery, useCreateSystemUserMutation, useUpdateSystemUserMutation, useGetRolesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';

const AdminSystemUserForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  
  const { data, isLoading } = useGetSystemUserQuery(id!, { skip: !isEdit });
  const { data: rolesData } = useGetRolesQuery({});
  const [createSystemUser, { isLoading: isCreating }] = useCreateSystemUserMutation();
  const [updateSystemUser, { isLoading: isUpdating }] = useUpdateSystemUserMutation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_id: '',
  });

  useEffect(() => {
    if (isEdit && data?.data) {
      const user = data.data;
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        phone: user.phone || '',
        role_id: user.role_id?.toString() || '',
      });
    }
  }, [isEdit, data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || formData.name.trim().length < 3) {
      showError('Name is required and must be at least 3 characters long.');
      return;
    }
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      showError('Please enter a valid email address.');
      return;
    }

    if (!isEdit && (!formData.password || formData.password.length < 6)) {
      showError('Password is required and must be at least 6 characters long.');
      return;
    }

    if (!formData.role_id) {
      showError('Please select a role.');
      return;
    }
    
    try {
      const submitData: any = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        role_id: parseInt(formData.role_id),
        phone: formData.phone.trim() || null,
      };

      if (isEdit) {
        if (formData.password) {
          submitData.password = formData.password;
        }
        const result = await updateSystemUser({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || 'System user updated successfully!');
      } else {
        submitData.password = formData.password;
        const result = await createSystemUser(submitData).unwrap();
        showSuccess(result.message || 'System user created successfully!');
      }
      
      navigate('/admin/system-users');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || 'Validation failed');
      } else {
        showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} system user. Please try again.`);
      }
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  const roles = (rolesData?.data?.data || []).filter((role: any) => role.status === 1);

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit System User' : 'Add New System User'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Dashboard</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/system-users'); }} className="text-muted text-hover-primary">System Users</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">User Name</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter System User Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">User Email</label>
                      <input
                        type="email"
                        className="form-control form-control-solid"
                        placeholder="Enter System User Email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className={`fs-6 fw-semibold mb-2 ${!isEdit ? 'required' : ''}`}>Password</label>
                      <input
                        type="password"
                        className="form-control form-control-solid"
                        placeholder={isEdit ? "Leave blank to keep current password" : "Enter System User Password"}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required={!isEdit}
                        minLength={isEdit ? undefined : 6}
                      />
                      {isEdit && <div className="form-text">Leave blank to keep current password</div>}
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="fs-6 fw-semibold mb-2">User Phone No</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter System User Phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">User Role</label>
                      <select
                        className="form-select form-control-solid"
                        name="role_id"
                        value={formData.role_id}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Role</option>
                        {roles.map((role: any) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/system-users')}>
                      Discard
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">Submit</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystemUserForm;

